import type { Folder } from "~/types/api";

const STORAGE_KEY_EXPANDED = "sidebar-expanded-state";
const STORAGE_KEY_FOLDERS = "sidebar-folders-cache";
const CACHE_TTL = 60 * 60 * 1000;

type FolderCache = {
  data: Folder[];
  timestamp: number;
};

type FolderCacheMap = {
  [brandId: string]: FolderCache;
};

export const sidebarStorage = {
  saveExpandedState(expandedIds: Set<string>): void {
    try {
      sessionStorage.setItem(STORAGE_KEY_EXPANDED, JSON.stringify([...expandedIds]));
    } catch (error) {
      console.error("Failed to save sidebar expanded state:", error);
    }
  },

  loadExpandedState(): Set<string> {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY_EXPANDED);
      if (stored) {
        const parsed = JSON.parse(stored) as string[];
        return new Set(parsed);
      }
    } catch (error) {
      console.error("Failed to load sidebar expanded state:", error);
    }
    return new Set<string>();
  },

  loadFolderCache(): FolderCacheMap {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY_FOLDERS);
      if (stored) {
        return JSON.parse(stored) as FolderCacheMap;
      }
    } catch (error) {
      console.error("Failed to load folder cache:", error);
    }
    return {};
  },

  cacheFolders(brandId: number, folders: Folder[]): void {
    try {
      const cache = this.loadFolderCache();
      cache[brandId] = {
        data: folders,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(STORAGE_KEY_FOLDERS, JSON.stringify(cache));
    } catch (error) {
      console.error("Failed to cache folders:", error);
    }
  },

  getCachedFolders(brandId: number): Folder[] | null {
    try {
      const cache = this.loadFolderCache();
      const brandCache = cache[brandId];

      if (!brandCache) {
        return null;
      }

      const isExpired = Date.now() - brandCache.timestamp > CACHE_TTL;
      if (isExpired) {
        delete cache[brandId];
        sessionStorage.setItem(STORAGE_KEY_FOLDERS, JSON.stringify(cache));
        return null;
      }

      return brandCache.data;
    } catch (error) {
      console.error("Failed to get cached folders:", error);
      return null;
    }
  },

  toggleExpanded(entityId: string): Set<string> {
    const expandedIds = this.loadExpandedState();
    if (expandedIds.has(entityId)) {
      expandedIds.delete(entityId);
    } else {
      expandedIds.add(entityId);
    }
    this.saveExpandedState(expandedIds);
    return expandedIds;
  },

  clearExpiredCache(): void {
    try {
      const cache = this.loadFolderCache();
      const now = Date.now();
      let hasChanges = false;

      for (const brandId in cache) {
        const brandCache = cache[brandId];
        if (brandCache && now - brandCache.timestamp > CACHE_TTL) {
          delete cache[brandId];
          hasChanges = true;
        }
      }

      if (hasChanges) {
        sessionStorage.setItem(STORAGE_KEY_FOLDERS, JSON.stringify(cache));
      }
    } catch (error) {
      console.error("Failed to clear expired cache:", error);
    }
  },

  clear(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY_EXPANDED);
      sessionStorage.removeItem(STORAGE_KEY_FOLDERS);
    } catch (error) {
      console.error("Failed to clear sidebar storage:", error);
    }
  },
};
